"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { PAGINATION } from "@unreserved/constants";
import type { ListingHomeApiResponse } from "@unreserved/sdk";
import { listingApi } from "@unreserved/services";

export const OWNED_LISTINGS_QUERY_KEY = ["listings", "owned", "infinite"] as const;

export interface UseOwnedListingsInfiniteParams {
  /** Page size. Defaults to PAGINATION.DEFAULT_LIMIT */
  pageSize?: number;
}

function getNextPageParam(lastPage: ListingHomeApiResponse): number | undefined {
  const pagination = lastPage.data?.pagination;
  if (!pagination) return undefined;
  const { pageNo, totalPages } = pagination;
  return pageNo < totalPages ? pageNo + 1 : undefined;
}

/**
 * Fetches paginated owned listings (GET /v1/listings/owned) with useInfiniteQuery
 * for infinite scroll / load more.
 */
export function useOwnedListingsInfinite(params?: UseOwnedListingsInfiniteParams) {
  const pageSize = params?.pageSize ?? PAGINATION.DEFAULT_LIMIT;

  return useInfiniteQuery<ListingHomeApiResponse>({
    queryKey: [...OWNED_LISTINGS_QUERY_KEY, pageSize],
    queryFn: ({ pageParam }) =>
      listingApi.listingControllerMarkListingAsOwnedV1({
        pageNo: (pageParam as number) ?? 1,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam,
    staleTime: 1000 * 60 * 5,
  });
}
